import React, { createContext, useContext, useEffect, useState } from "react";
export type Currency = "USD" | "JMD";
type Rate = { value: number; updated: number };
const MAX_AGE = 48 * 60 * 60 * 1000;
export function validRate(rate: Rate | null, now = Date.now()): rate is Rate {
  return (
    !!rate &&
    Number.isFinite(rate.value) &&
    rate.value > 0 &&
    Number.isFinite(rate.updated) &&
    rate.updated <= now &&
    now - rate.updated < MAX_AGE
  );
}
export function formatPrice(usd: number, currency: Currency, rate = 1) {
  if (!Number.isFinite(usd)) return "Price unavailable";
  return `${currency === "JMD" ? "J$" : "$"}${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(usd * (currency === "JMD" ? rate : 1))}`;
}
function read(key: string) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function save(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* Preference still works for this visit. */
  }
}
const Context = createContext({
  currency: "USD" as Currency,
  select: (_c: Currency) => {},
  money: (n: number) => formatPrice(n, "USD"),
  rate: null as Rate | null,
  loading: false,
  error: false,
});
export const useCurrency = () => useContext(Context);
export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const [selected, setSelected] = useState<Currency>(() =>
    read("store-currency") === "JMD" ? "JMD" : "USD"
  );
  const [rate, setRate] = useState<Rate | null>(() => {
    try {
      const r = JSON.parse(read("store-usd-jmd-rate") || "null");
      return validRate(r) ? r : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);
  const available = validRate(rate, now);
  useEffect(() => {
    if (selected !== "JMD" || available) return;
    const controller = new AbortController();
    let live = true;
    setLoading(true);
    setError(false);
    const timer = setTimeout(() => controller.abort(), 8000);
    fetch("https://open.er-api.com/v6/latest/USD", {
      signal: controller.signal,
    })
      .then((r) => {
        if (!r.ok) throw Error();
        return r.json();
      })
      .then((data) => {
        const next = {
          value: data.rates?.JMD,
          updated: data.time_last_update_unix * 1000,
        };
        if (
          data.result !== "success" ||
          data.base_code !== "USD" ||
          !validRate(next)
        )
          throw Error();
        if (live) {
          setRate(next);
          // The rate can be stamped later than the last clock tick; refresh it
          // so a just-fetched rate isn't treated as from the future.
          setNow(Date.now());
          save("store-usd-jmd-rate", JSON.stringify(next));
        }
      })
      .catch(() => {
        if (live) setError(true);
      })
      .finally(() => {
        clearTimeout(timer);
        if (live) setLoading(false);
      });
    return () => {
      live = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [selected, available]);
  const currency: Currency = selected === "JMD" && available ? "JMD" : "USD";
  return (
    <Context.Provider
      value={{
        currency,
        rate: available ? rate : null,
        loading,
        error,
        select: (c) => {
          setSelected(c);
          save("store-currency", c);
        },
        money: (n) => formatPrice(n, currency, rate?.value),
      }}
    >
      {children}
    </Context.Provider>
  );
}
export function CurrencySelector({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`currency-control${compact ? " currency-compact" : ""}`}>
      <CurrencyPicker />
      {!compact && <CurrencyNotes />}
    </div>
  );
}
/** Rate and fallback messages; renders nothing while prices show in USD. */
export function CurrencyNotice() {
  const { currency, loading, error, rate } = useCurrency();
  if (!loading && !error && !(currency === "JMD" && rate)) return null;
  return (
    <div className="currency-control currency-notice">
      <CurrencyNotes />
    </div>
  );
}
function CurrencyPicker() {
  const { currency, select, loading } = useCurrency();
  return (
    <label>
      <span className="currency-label">Currency</span>{" "}
      <select
        aria-label="Display currency"
        value={loading ? "JMD" : currency}
        onChange={(e) => select(e.target.value as Currency)}
      >
        <option value="USD">USD</option>
        <option value="JMD">JMD</option>
      </select>
    </label>
  );
}
function CurrencyNotes() {
  const { currency, loading, error, rate } = useCurrency();
  return (
    <>
      {loading && <span role="status">Loading JMD rate…</span>}
      {error && (
        <span role="status">JMD rate unavailable. Prices shown in USD.</span>
      )}
      {currency === "JMD" && rate && (
        <span>
          Approx. JMD · US$1 = J${rate.value.toFixed(2)} · Rate{" "}
          {new Date(rate.updated).toLocaleDateString("en-GB")} · Card checkout in USD; JMD bank transfers use a locked rate.{" "}
          <a
            href="https://www.exchangerate-api.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            ExchangeRate-API
          </a>
        </span>
      )}
    </>
  );
}
